import {
  closestCenter,
  DndContext,
  type DndContextProps,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  defaultAnimateLayoutChanges,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  type Ref,
  useContext,
  useMemo,
} from 'react'
import { cn } from '../lib/cn'
import { DataList } from './data-list'

type SortableHandleContextValue = {
  attributes: DraggableAttributes
  disabled: boolean
  listeners: DraggableSyntheticListeners
  setActivatorNodeRef: (element: HTMLElement | null) => void
}

const SortableHandleContext = createContext<SortableHandleContextValue | null>(null)

function animateLayoutChangesWhileSorting(
  options: Parameters<typeof defaultAnimateLayoutChanges>[0],
) {
  return options.isSorting && defaultAnimateLayoutChanges(options)
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ref.current = value
  }
}

export function SortableDataList({
  accessibility,
  children,
  items,
  onReorder,
  ...props
}: ComponentProps<'ul'> & {
  accessibility: NonNullable<DndContextProps['accessibility']>
  appearance?: 'stacked' | 'divided'
  items: UniqueIdentifier[]
  onReorder: (activeId: UniqueIdentifier, overId: UniqueIdentifier) => void
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  return (
    <DndContext
      accessibility={accessibility}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) {
          onReorder(active.id, over.id)
        }
      }}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <DataList {...props}>{children}</DataList>
      </SortableContext>
    </DndContext>
  )
}

export function SortableDataListItem({
  children,
  className,
  disabled = false,
  id,
  ref,
  style,
  ...props
}: Omit<ComponentProps<'li'>, 'id'> & {
  children: ReactNode
  disabled?: boolean
  id: UniqueIdentifier
}) {
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    disabled,
    animateLayoutChanges: animateLayoutChangesWhileSorting,
  })
  const handleContext = useMemo(
    () => ({ attributes, disabled, listeners, setActivatorNodeRef }),
    [attributes, disabled, listeners, setActivatorNodeRef],
  )
  return (
    <SortableHandleContext.Provider value={handleContext}>
      <li
        {...props}
        ref={(node) => {
          setNodeRef(node)
          assignRef(ref, node)
        }}
        data-slot="sortable-data-list-item"
        data-dragging={isDragging || undefined}
        className={cn('min-w-0', isDragging && 'relative opacity-60', className)}
        style={{
          ...style,
          transform: CSS.Transform.toString(transform),
          transition,
          zIndex: isDragging ? 1 : style?.zIndex,
        }}
      >
        {children}
      </li>
    </SortableHandleContext.Provider>
  )
}

export function SortableDataListHandle({
  children,
  className,
  label,
  ...props
}: Omit<ComponentProps<'button'>, 'children'> & {
  children?: ReactNode
  label: string
}) {
  const context = useContext(SortableHandleContext)
  if (!context) {
    throw new Error('SortableDataListHandle must be used inside SortableDataListItem.')
  }
  return (
    <button
      {...props}
      {...context.attributes}
      {...context.listeners}
      ref={context.setActivatorNodeRef}
      type="button"
      disabled={context.disabled || props.disabled}
      aria-label={label}
      className={cn(
        'grid size-11 shrink-0 touch-none cursor-grab place-items-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
    >
      {children ?? <GripVertical aria-hidden="true" className="size-4" />}
    </button>
  )
}
